import React, { useEffect, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { RootState, storeActions } from '../../store'
import { CurrentSubView, ListStatus, RecognitionState, RetorikEvent, Routes } from '../../models/enums'

interface SendTypingIndicatorProps {
  isRetorikNews: boolean
}

const typingTimer = 2000

const SendTypingIndicator = ({ isRetorikNews }: SendTypingIndicatorProps): JSX.Element => {
  const dispatch = useDispatch()
  const speaking = useSelector((state: RootState) => state.speechReducer.speaking)
  const streamingQueue = useSelector((state: RootState) => state.speechReducer.streamingQueue)
  const activeRecognitionState = useSelector((state: RootState) => state.speechReducer.activeRecognitionState)
  const route = useSelector((state: RootState) => state.viewReducer.route)
  const loaderClosed = useSelector((state: RootState) => state.retorikReducer.loaderClosed)
  const appAvailable = useSelector((state: RootState) => state.retorikReducer.appAvailable)
  const isWaitingForResponse = useSelector((state: RootState) => state.retorikReducer.isWaitingForResponse)
  const currentSubView = useSelector((state: RootState) => state.utilsReducer.currentSubView)
  const listStatus = useSelector((state: RootState) => state.utilsReducer.listStatus)
  const retorikEvent = useSelector((state: RootState) => state.utilsReducer.retorikEvent)
  const userIsTyping = useSelector((state: RootState) => state.utilsReducer.userIsTyping)
  const userIsSwiping = useSelector((state: RootState) => state.utilsReducer.userIsSwiping)
  const dataRef = useRef<boolean | null>(false)

  useEffect(() => {
    // Don't send typing activity when we are receiving streaming events
    const isReceivingStreamingData =
      !!streamingQueue.length &&
      !!streamingQueue.find((activity) => activity.name?.toLowerCase() === 'davi.streammessageactivity') &&
      !streamingQueue.find((activity) => activity.value?.streamingCount)

    dataRef.current =
      !isWaitingForResponse &&
      // Don't send typing activity when we are receiving streaming events
      !isReceivingStreamingData &&
      (!loaderClosed ||
        activeRecognitionState !== RecognitionState.Closed ||
        userIsTyping ||
        userIsSwiping ||
        speaking ||
        currentSubView !== CurrentSubView.none ||
        route === Routes.News ||
        retorikEvent === RetorikEvent.DetailViewOpen ||
        listStatus === ListStatus.opened)

    dispatch(storeActions.directline.setApplicationIsBusy(!!dataRef.current))
  }, [appAvailable, loaderClosed, activeRecognitionState, userIsTyping, userIsSwiping, speaking, streamingQueue, currentSubView, route, retorikEvent, listStatus, isWaitingForResponse])

  const checkShouldSendTypingIndicator = (): void => {
    dataRef.current && !isRetorikNews && dispatch(storeActions.directline.sendTyping())
  }

  useEffect(() => {
    const interval = setInterval(() => {
      checkShouldSendTypingIndicator()
    }, typingTimer)

    return () => {
      interval && clearInterval(interval)
    }
  }, [])

  return <React.Fragment />
}

export default SendTypingIndicator
